import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Typography,
} from "@mui/material";

interface Column {
  field: string;
  headerName: string;
  width?: number;
}

interface Props {
  rows: any[];
  columns: Column[];
  expandColumns?: Column[];
  expandable?: boolean;
  expandDataKey?: string;
  action?: (column: any, row: any) => JSX.Element | null;
  total?: number;
  fetchData?: (data: any) => void;
  resetPagination?: (res: any) => void;
}

const CustomTable: React.FC<Props> = ({
  rows = [],
  columns = [],
  expandColumns = [],
  expandable = false,
  expandDataKey,
  action,
  total = 0,
  fetchData,
  resetPagination,
}) => {
  const [expandedRow, setExpandedRow] = useState<any>(null);

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    resetPagination?.(pagination);
  }, [pagination]);

  const handlePageChange = (_: any, page: number) => {
    const updated = { ...pagination, pageNumber: page };
    setPagination(updated);
    fetchData?.(updated);
  };

  const handlePageSize = (size: number) => {
    const updated = { pageNumber: 1, pageSize: size };
    setPagination(updated);
    fetchData?.(updated);
  };

  return (
    <Box mt={2}>
      <TableContainer component={Paper} sx={{ border: "1px solid #000" }}>
        <Table
          sx={{
            tableLayout: "fixed", // 🔥 KEY FIX
            width: "100%",
          }}
        >
          {/* HEADER */}
          <TableHead>
            <TableRow sx={{ backgroundColor: "#b7cbd9" }}>
              {expandable && <TableCell sx={{ width: 50 }} />}

              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align="center"
                  sx={{
                    fontWeight: 700,
                    padding: "16px",
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {rows.map((row, index) => (
              <React.Fragment key={row.id || index}>
                <TableRow
                    sx={{
                      borderBottom: "1px solid #e0e0e0", // ✅ row-level border
                      "& td": {
                        padding: "16px",
                        borderBottom: "none", // ✅ remove per-cell lines
                      },
                    }}
                  >
                  {expandable && (
                    <TableCell>
                      <IconButton
                        onClick={() =>
                          setExpandedRow(
                            expandedRow === row.id ? null : row.id
                          )
                        }
                      >
                        {expandedRow === row.id ? "⌃" : "⌄"}
                      </IconButton>
                    </TableCell>
                  )}

                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align="center"
                      sx={{
                        width: col.width,
                        minWidth: col.width,
                        maxWidth: col.width,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {action
                        ? action(col.field, row) ?? row[col.field]
                        : row[col.field]}
                    </TableCell>
                  ))}
                </TableRow>

                {/* EXPAND */}
                {expandable && expandedRow === row.id && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Box
                        sx={{
                          border: "1px solid #000",
                          margin: "10px",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Table>
                          <TableHead>
                            <TableRow>
                              {expandColumns.map((col) => (
                                <TableCell
                                  key={col.field}
                                  align="center"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {col.headerName}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {row[expandDataKey || ""]?.map(
                              (child: any, i: number) => (
                                <TableRow key={i}>
                                  {expandColumns.map((col) => (
                                    <TableCell key={col.field} align="center">
                                      {child[col.field]}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* FOOTER */}
      <Paper
        sx={{
          mt: 1,
          px: 2,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: "1px solid #000",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>Rows per page:</Typography>
          <FormControl size="small">
            <Select
              value={pagination.pageSize}
              onChange={(e) =>
                handlePageSize(Number(e.target.value))
              }
            >
              {[10, 20, 50].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Pagination
          count={Math.ceil(total / pagination.pageSize) || 1}
          page={pagination.pageNumber}
          onChange={handlePageChange}
        />
      </Paper>
    </Box>
  );
};

export default CustomTable;